import { useEffect, type ReactNode, type RefObject } from 'react'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { BottomSheet } from './BottomSheet'
import { useNls } from '../../nls/nlsProvider'

export interface MoreTabSheetItem {
  key: string
  label: string
  icon?: ReactNode
  unread?: boolean
  locked?: boolean
  lockReason?: string
  testId?: string
}

export interface MoreTabsSheetProps {
  isOpen: boolean
  onClose: () => void
  tabs: MoreTabSheetItem[]
  onSelectTab: (key: string) => void
  onOpenEdit: () => void
  moreButtonRef?: RefObject<HTMLElement | null>
  title?: string
  editButtonLabel?: string
}

/**
 * Mobile bottom sheet presenting secondary tabs in the 4 + More navigation pattern.
 * Displays tabs in a grid, indicates locked tabs with reasons, provides an "Edit bar"
 * entry point, and traps focus with return to More on dismissal (TB2, TB-P9).
 */
export const MoreTabsSheet = ({
  isOpen,
  onClose,
  tabs,
  onSelectTab,
  onOpenEdit,
  moreButtonRef,
  title,
  editButtonLabel,
}: MoreTabsSheetProps) => {
  const { t } = useNls()

  useEffect(() => {
    if (!isOpen && moreButtonRef?.current) {
      moreButtonRef.current.focus()
    }
  }, [isOpen, moreButtonRef])

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      dismissible={true}
      maxHeightSvh={78}
      className="moreTabsSheet"
    >
      <div className="flex flex-col select-none">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)]">
          <h3 className="text-sm font-semibold text-[var(--text-strong)]">
            {title ?? t('NLSCommonTabMore')}
          </h3>
          <button
            type="button"
            onClick={onOpenEdit}
            className="text-xs font-semibold text-[var(--accent-strong)] hover:underline px-2 py-1 rounded transition-colors focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
            data-testid="more-sheet-edit-btn"
          >
            {editButtonLabel ?? t('NLSCommonTabEditBar')}
          </button>
        </div>

        <div
          className="grid grid-cols-4 gap-y-3 gap-x-1 p-3"
          role="menu"
          aria-label={t('NLSCommonTabMoreAriaLabel')}
          data-testid="more-tabs-grid"
        >
          {tabs.map((tab) => {
            if (tab.locked) {
              return (
                <div
                  key={tab.key}
                  role="menuitem"
                  className="flex flex-col items-center text-center gap-1.5 p-2 rounded-xl opacity-45 cursor-not-allowed"
                  aria-disabled="true"
                  data-testid={tab.testId ?? `more-tab-${tab.key}`}
                >
                  <div className="relative inline-flex items-center justify-center h-10 w-10 rounded-full bg-[var(--surface-raised)] text-[var(--muted)]">
                    <LockRoundedIcon fontSize="small" style={{ fontSize: '20px' }} />
                  </div>
                  <span className="text-[11.5px] font-medium text-[var(--muted)] truncate max-w-full">
                    {tab.label}
                  </span>
                  {tab.lockReason ? (
                    <span
                      className="text-[10px] text-[var(--muted)] truncate max-w-full px-0.5"
                      title={tab.lockReason}
                    >
                      {tab.lockReason}
                    </span>
                  ) : null}
                </div>
              )
            }

            return (
              <button
                key={tab.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  onSelectTab(tab.key)
                  onClose()
                }}
                className="relative flex flex-col items-center text-center gap-1.5 p-2 rounded-xl text-[var(--text-strong)] hover:bg-[var(--surface-raised)] active:scale-95 transition-all group focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                data-testid={tab.testId ?? `more-tab-${tab.key}`}
              >
                <div className="relative inline-flex items-center justify-center h-10 w-10 rounded-full bg-[var(--surface-raised)] group-hover:bg-[var(--accent-soft)] group-hover:text-[var(--accent-strong)] text-[var(--text-strong)] transition-colors">
                  {tab.icon}
                  {tab.unread ? (
                    <span
                      className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--danger)] ring-1 ring-[var(--surface-card)]"
                      aria-label="unseen"
                      data-testid={`${tab.testId ?? `more-tab-${tab.key}`}-unread`}
                    />
                  ) : null}
                </div>
                <span className="text-[11.5px] font-medium text-[var(--text-strong)] group-hover:text-[var(--accent-strong)] truncate max-w-full">
                  {tab.label}
                </span>
              </button>
            )
          })}
        </div>

        <div className="px-4 py-2.5 text-center text-[11px] text-[var(--muted)] border-t border-[var(--border)]">
          {t('NLSCommonTabMoreHint')}
        </div>
      </div>
    </BottomSheet>
  )
}
